/**
 * Frequency Role - Constants
 *
 * Fatores de conversão, símbolos e aliases para unidades de frequência.
 *
 * Fontes:
 * - SI Brochure, 9th Edition (2019)
 * - NIST SP 811
 */

import type { SimpleUnitConfig, UnitAliases } from "../../contracts";

// =============================================================================
// TYPES
// =============================================================================

export type FrequencyUnit =
  // SI
  | "terahertz"
  | "gigahertz"
  | "megahertz"
  | "kilohertz"
  | "hertz"
  | "millihertz"
  | "microhertz"
  // Other
  | "rpm"
  | "bpm"
  | "radians_per_second"
  | "cycles_per_minute";

// =============================================================================
// UNITS CONFIG
// =============================================================================

/**
 * Configuração de cada unidade de frequência.
 *
 * Base: hertz (Hz) = 1 ciclo por segundo
 *
 * Relações:
 * - 1 RPM = 1/60 Hz (rotações por minuto)
 * - 1 rad/s = 1/(2π) Hz
 */
export const FREQUENCY_UNITS: Record<FrequencyUnit, SimpleUnitConfig> = {
  // SI Units (todos exatos)
  terahertz: {
    factor: 1e12,
    symbol: "THz",
    singular: "terahertz",
    plural: "terahertz",
  },
  gigahertz: {
    factor: 1e9,
    symbol: "GHz",
    singular: "gigahertz",
    plural: "gigahertz",
  },
  megahertz: {
    factor: 1e6,
    symbol: "MHz",
    singular: "megahertz",
    plural: "megahertz",
  },
  kilohertz: {
    factor: 1000,
    symbol: "kHz",
    singular: "kilohertz",
    plural: "kilohertz",
  },
  hertz: {
    factor: 1,
    symbol: "Hz",
    singular: "hertz",
    plural: "hertz",
  },
  millihertz: {
    factor: 0.001,
    symbol: "mHz",
    singular: "millihertz",
    plural: "millihertz",
  },
  microhertz: {
    factor: 0.000001,
    symbol: "μHz",
    singular: "microhertz",
    plural: "microhertz",
  },

  // Other units
  rpm: {
    factor: 1 / 60, // 1 RPM = 1/60 Hz
    symbol: "rpm",
    singular: "revolution per minute",
    plural: "revolutions per minute",
  },
  bpm: {
    factor: 1 / 60, // 1 BPM = 1/60 Hz
    symbol: "bpm",
    singular: "beat per minute",
    plural: "beats per minute",
  },
  radians_per_second: {
    factor: 1 / (2 * Math.PI), // 1 rad/s = 1/(2π) Hz ≈ 0.159154943
    symbol: "rad/s",
    singular: "radian per second",
    plural: "radians per second",
  },
  cycles_per_minute: {
    factor: 1 / 60, // same as RPM
    symbol: "cpm",
    singular: "cycle per minute",
    plural: "cycles per minute",
  },
};

// =============================================================================
// ALIASES
// =============================================================================

/**
 * Aliases para reconhecimento de unidades no cast
 */
export const FREQUENCY_ALIASES: UnitAliases = {
  // terahertz
  thz: "terahertz",
  // gigahertz
  ghz: "gigahertz",
  // megahertz
  mhz: "megahertz",
  // kilohertz
  khz: "kilohertz",
  // hertz
  hz: "hertz",
  cps: "hertz",
  // microhertz
  "μhz": "microhertz",
  uhz: "microhertz",
  // rpm
  rpm: "rpm",
  // bpm
  bpm: "bpm",
  // radians_per_second
  "rad/s": "radians_per_second",
  // cycles_per_minute
  cpm: "cycles_per_minute",
};

// =============================================================================
// HELPERS
// =============================================================================

/** Lista de todas as unidades */
export const FREQUENCY_UNIT_LIST = Object.keys(
  FREQUENCY_UNITS,
) as FrequencyUnit[];

/** Fatores de conversão (para compatibilidade) */
export const FREQUENCY_FACTORS: Record<FrequencyUnit, number> =
  Object.fromEntries(
    Object.entries(FREQUENCY_UNITS).map(([unit, config]) => [
      unit,
      config.factor ?? 1,
    ]),
  ) as Record<FrequencyUnit, number>;
